import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Users, Map, Building2, ScrollText, NotebookPen, CalendarClock, Swords, BookOpen } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { AddItem } from '../types/actions';
import type { CampaignData, CollectionKey, WikiTemplate } from '../../shared/types';
import NPCForm from './forms/NPCForm';
import LocationForm from './forms/LocationForm';
import OrganizationForm from './forms/OrganizationForm';
import PlotThreadForm from './forms/PlotThreadForm';
import SessionNoteForm from './forms/SessionNoteForm';
import FutureContentForm from './forms/FutureContentForm';
import PlayerCharacterForm from './forms/PlayerCharacterForm';
import WikiArticleForm from './forms/WikiArticleForm';
import { api } from '../api/client';

interface QuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  addItem: AddItem;
  campaignData: CampaignData;
  updateCampaignInfo: <K extends keyof CampaignData>(field: K, value: CampaignData[K]) => void;
  campaignRole?: string | null;
}

const TABS: { id: CollectionKey; label: string; icon: LucideIcon }[] = [
  { id: 'npcs', label: 'NPC', icon: Users },
  { id: 'locations', label: 'Location', icon: Map },
  { id: 'organizations', label: 'Organization', icon: Building2 },
  { id: 'plotThreads', label: 'Plot', icon: ScrollText },
  { id: 'sessionNotes', label: 'Session', icon: NotebookPen },
  { id: 'futureContent', label: 'Future', icon: CalendarClock },
  { id: 'playerCharacters', label: 'Character', icon: Swords },
  { id: 'wikiArticles', label: 'Wiki', icon: BookOpen },
];

export default function QuickAddModal({
  isOpen,
  onClose,
  addItem,
  campaignData,
  updateCampaignInfo,
  campaignRole = null,
}: QuickAddModalProps) {
  const [activeTab, setActiveTab] = useState<CollectionKey>('npcs');
  const [wikiTemplates, setWikiTemplates] = useState<WikiTemplate[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const canSetGmVisibility = campaignRole === 'owner' || campaignRole === 'gm';
  const handleClose = useCallback(() => {
    setActiveTab('npcs');
    onClose();
  }, [onClose]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) return;
    let isActive = true;
    const loadWikiTemplates = async () => {
      try {
        const data = await api.getWikiTemplates();
        if (isActive) {
          setWikiTemplates(data);
        }
      } catch {
        if (isActive) {
          setWikiTemplates([]);
        }
      }
    };
    void loadWikiTemplates();
    return () => {
      isActive = false;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div className="card-modal-backdrop" onClick={handleClose} />
      <div ref={modalRef} className="search-modal" style={{ maxWidth: '800px' }}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-default)] bg-[var(--bg-secondary)]">
          <h2 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-2">
            <span className="p-1 bg-[var(--accent-muted)] text-[var(--accent)] rounded">
              {(() => {
                const Icon = TABS.find(t => t.id === activeTab)?.icon || Users;
                return <Icon size={18} />;
              })()}
            </span>
            Quick Add: {TABS.find(t => t.id === activeTab)?.label}
          </h2>
          <button onClick={handleClose} className="icon-button">
            <X size={20} />
          </button>
        </div>

        <div className="flex border-b border-[var(--border-default)] overflow-x-auto hide-scrollbar bg-[var(--bg-tertiary)]">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap
                  ${isActive 
                    ? 'text-[var(--accent)] border-b-2 border-[var(--accent)] bg-[var(--accent-muted)]' 
                    : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                  }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-6 overflow-y-auto bg-[var(--bg-tertiary)] flex-1">
          {activeTab === 'npcs' && (
            <NPCForm 
              addItem={addItem} 
              locations={campaignData.locations} 
            />
          )}
          {activeTab === 'locations' && (
            <LocationForm addItem={addItem} />
          )}
          {activeTab === 'organizations' && (
            <OrganizationForm addItem={addItem} />
          )}
          {activeTab === 'plotThreads' && (
            <PlotThreadForm addItem={addItem} />
          )}
          {activeTab === 'sessionNotes' && (
            <SessionNoteForm 
              addItem={addItem} 
              sessionNumber={campaignData.sessionNumber}
              updateCampaignInfo={updateCampaignInfo}
              canSetGmVisibility={canSetGmVisibility}
            />
          )}
          {activeTab === 'futureContent' && (
            <FutureContentForm addItem={addItem} />
          )}
           {activeTab === 'playerCharacters' && (
            <PlayerCharacterForm addItem={addItem} />
          )}
          {activeTab === 'wikiArticles' && (
            <WikiArticleForm
              addItem={addItem}
              articles={campaignData.wikiArticles || []}
              canSetGmVisibility={canSetGmVisibility}
              templates={wikiTemplates}
            />
          )}
        </div>
      </div>
    </>
  );
}
